/*!
 * Copyright (c) 2026 Elevation Technologies Pte Ltd.
 * Developer: filimoni@elevationtech.com.fj
 */
import React, { useState } from 'react';
import PhotoLightbox from '../components/PhotoLightbox';
import VideoFrame from '../components/VideoFrame';
import { GLOBAL_NETWORK_IMAGES, GLOBAL_NETWORK_PHOTOS } from '../data/siteData';

export function GlobalNetworkCopy({ html, className = '' }) {
  if (!html) {
    return null;
  }

  return <div className={`fsf-global-copy ${className}`.trim()} dangerouslySetInnerHTML={{ __html: html }} />;
}

export function GlobalNetworkMedia({ photo, onOpen, className = '', loading = 'lazy' }) {
  if (!photo?.src) {
    return null;
  }

  return (
    <figure className={`fsf-global-media ${className}`.trim()}>
      <button
        className="fsf-global-media__button"
        type="button"
        onClick={() => onOpen(photo)}
        aria-label={`Open ${photo.alt}`}
      >
        <img src={photo.src} alt={photo.alt} loading={loading} />
      </button>
    </figure>
  );
}

export default function GlobalNetworkPageSections({ page }) {
  const [activePhotoIndex, setActivePhotoIndex] = useState(null);
  const copyBlocks = (page.blocks || []).filter((block) => block?.type === 'html');
  const videoBlocks = (page.blocks || []).filter((block) => block?.type === 'video');

  const openPhoto = (photo) => {
    const photoIndex = GLOBAL_NETWORK_PHOTOS.findIndex((item) => item.src === photo.src);

    if (photoIndex >= 0) {
      setActivePhotoIndex(photoIndex);
    }
  };

  return (
    <>
      <div className="fsf-global-story">
        <section className="fsf-global-section fsf-global-section--intro">
          <div className="fsf-global-section__inner fsf-global-intro">
            <div className="fsf-global-intro__copy">
              <p className="fsf-section-kicker">Global Cargo Hub</p>
              <h2>Connecting the Pacific to trusted global partners</h2>
              <GlobalNetworkCopy html={copyBlocks[0]?.html} />
            </div>
            <GlobalNetworkMedia
              photo={GLOBAL_NETWORK_IMAGES.oceanHub}
              onOpen={openPhoto}
              className="fsf-global-media--feature"
              loading="eager"
            />
          </div>
        </section>

        <section className="fsf-global-section fsf-global-section--partner fsf-global-section--fidi">
          <div className="fsf-global-section__inner">
            <GlobalNetworkMedia
              photo={GLOBAL_NETWORK_IMAGES.fidiHeader}
              onOpen={openPhoto}
              className="fsf-global-media--banner"
              loading="eager"
            />
            <div className="fsf-global-two">
              <GlobalNetworkCopy html={copyBlocks[1]?.html} />
              <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.fidiSeal} onOpen={openPhoto} className="fsf-global-media--support" />
            </div>
          </div>
        </section>

        <section className="fsf-global-section fsf-global-section--partner fsf-global-section--iam fsf-global-section--soft">
          <div className="fsf-global-section__inner">
            <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.iamHeader} onOpen={openPhoto} className="fsf-global-media--banner" />
            <div className="fsf-global-two fsf-global-two--visual-left">
              <div className="fsf-global-stack fsf-global-stack--narrow">
                <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.iamDirectory} onOpen={openPhoto} className="fsf-global-media--portrait" />
                <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.iamTrusted} onOpen={openPhoto} className="fsf-global-media--badge" />
              </div>
              <div className="fsf-global-copy-group">
                <GlobalNetworkCopy html={copyBlocks[2]?.html} />
                <GlobalNetworkCopy html={copyBlocks[3]?.html} className="fsf-global-copy--note" />
              </div>
            </div>
          </div>
        </section>

        <section className="fsf-global-section fsf-global-section--partner fsf-global-section--iata">
          <div className="fsf-global-section__inner">
            <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.iataHeader} onOpen={openPhoto} className="fsf-global-media--banner" />
            <div className="fsf-global-feature-grid">
              <div className="fsf-global-copy-group">
                <GlobalNetworkCopy html={copyBlocks[4]?.html} className="fsf-global-copy--lead" />
                <GlobalNetworkCopy html={copyBlocks[5]?.html} />
              </div>
              <div className="fsf-global-stack">
                <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.iataListing} onOpen={openPhoto} className="fsf-global-media--wide" />
                <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.iataJourney} onOpen={openPhoto} className="fsf-global-media--small" />
              </div>
            </div>
          </div>
        </section>

        <section className="fsf-global-section fsf-global-section--partner fsf-global-section--dfa fsf-global-section--soft">
          <div className="fsf-global-section__inner">
            <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.dfaHeader} onOpen={openPhoto} className="fsf-global-media--banner" />
            <div className="fsf-global-copy-grid">
              <GlobalNetworkCopy html={copyBlocks[6]?.html} />
              <GlobalNetworkCopy html={copyBlocks[7]?.html} />
            </div>
            <div className="fsf-global-two">
              <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.dfaNetwork} onOpen={openPhoto} className="fsf-global-media--feature" />
              <div className="fsf-global-stack">
                <GlobalNetworkCopy html={copyBlocks[8]?.html} />
                <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.dfaLogo} onOpen={openPhoto} className="fsf-global-media--logo" />
              </div>
            </div>
          </div>
        </section>

        <section className="fsf-global-section fsf-global-section--partner fsf-global-section--fiata">
          <div className="fsf-global-section__inner">
            <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.fiataHeader} onOpen={openPhoto} className="fsf-global-media--banner" />
            <div className="fsf-global-two fsf-global-two--visual-left">
              <div className="fsf-global-stack">
                <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.fiataFreight} onOpen={openPhoto} className="fsf-global-media--feature" />
                <div className="fsf-global-mini-row">
                  <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.fiataGlobe} onOpen={openPhoto} className="fsf-global-media--mini" />
                  <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.fiataTagline} onOpen={openPhoto} className="fsf-global-media--mini fsf-global-media--tagline" />
                </div>
              </div>
              <div className="fsf-global-copy-group">
                <GlobalNetworkCopy html={copyBlocks[9]?.html} />
                <GlobalNetworkCopy html={copyBlocks[10]?.html} />
              </div>
            </div>
          </div>
        </section>

        <section className="fsf-global-section fsf-global-section--partner fsf-global-section--ungc fsf-global-section--soft">
          <div className="fsf-global-section__inner">
            <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.ungcHeader} onOpen={openPhoto} className="fsf-global-media--banner" />
            <GlobalNetworkCopy html={copyBlocks[11]?.html} />
            <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.ungcMission} onOpen={openPhoto} className="fsf-global-media--wide" />
          </div>
        </section>

        <section className="fsf-global-section fsf-global-section--partner fsf-global-section--cargowise">
          <div className="fsf-global-section__inner">
            <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.cargowiseHeader} onOpen={openPhoto} className="fsf-global-media--banner" />
            <GlobalNetworkCopy html={copyBlocks[12]?.html} />
            <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.cargowiseBadges} onOpen={openPhoto} className="fsf-global-media--logo" />
            <GlobalNetworkCopy html={copyBlocks[13]?.html} />
            <GlobalNetworkMedia photo={GLOBAL_NETWORK_IMAGES.cargowiseIntegration} onOpen={openPhoto} className="fsf-global-media--wide" />
          </div>
        </section>

        {videoBlocks.length ? (
          <section className="fsf-global-section fsf-global-section--video">
            <div className="fsf-global-section__inner">
              <VideoFrame url={videoBlocks[0].url} title="Freight Services Fiji global network video" />
            </div>
          </section>
        ) : null}
      </div>

      <PhotoLightbox
        images={GLOBAL_NETWORK_PHOTOS}
        activeIndex={activePhotoIndex}
        setActiveIndex={setActivePhotoIndex}
        label="Freight Services Fiji global network photos"
      />
    </>
  );
}
